<template>
    <AppPageFrame showHeader login="require">
        <LoadingPage v-if="!form || !moduleState || !draftId" />

        <div v-else-if="availableApplicationTypes.length == 0" class="formPage">
            <div class="notification bulmaNotification is-warning mt-4">
                <p>There are no applications available for you to start</p>
            </div>

            <div class="is-flex is-flex-direction-column is-align-items-center">
                <SaveButton large label="Return To Home Page" @click="$router.push('/')"/>
            </div>
        </div>

        <div v-else class="formPage">

            <!-- Header section -->
            <div v-if="currentSection">
                <h1>{{ currentSection?.name }}</h1>
                <progress v-if="currentSection != null" class="progress is-primary mt-5" :value="5 + currentSection.progress * 90" max="100"></progress>
            </div>
            
            <!-- Page: Select Application Types -->
            <div v-if="form.state.activePage == 'selectBenefitTypes' && moduleState.application.selectedBenefits">
                <h2 class="pageHeading">Which benefits would you like to apply for?</h2>

                <FormCheckboxField boxLabel="Food Stamps" v-model="moduleState.application.selectedBenefits.foodStamps"/>

                <FormCheckboxField boxLabel="Temporary Assistance for Needs Families (TANF)" v-model="moduleState.application.selectedBenefits.TANF"/>
                <FormCheckboxField class="ml-3" boxLabel="Grandparents Raising Grandchildren (GRG)" :isDisabled="!moduleState.application.selectedBenefits.TANF" v-model="moduleState.application.selectedBenefits.GRG"/>
                
                <FormCheckboxField boxLabel="Refugee Cash Assistance" v-model="moduleState.application.selectedBenefits.refugeeCashAssistance"/>
                <FormCheckboxField boxLabel="Medicaid" v-model="moduleState.application.selectedBenefits.medicaid"/>

                <FormCheckboxField boxLabel="Register To Vote" v-model="moduleState.application.voterRegistrationIntent"/>
            </div>

            <!-- Page: TANF waiver -->
            <div v-if="form.state.activePage == 'tanfWaiver'">
                <div class="termsBox">
                    <h2 class="termsHeading">You must read and agree to the following declaration to continue</h2>

                    <div class="termsContent">
                        <p>In the TANF Program, an IPV (Intentional Program Violation) is an intentional action by an individual to establish or maintain an assistance unit's (AU's) eligibility, or to increase or prevent a decrease in the AU's benefits, by providing false or misleading information or withholding information.</p>
                        <ul>
                            <li class="mt-3">Any household member who hides information and does not report changes on time or does not tell the truth will lose TANF benefits for six months for the first violation, twelve months for the second violation and permanently for the third violation. The misuse of the cash assistance funds or TANF DEBIT card to withdraw cash or perform transactions at casinos, liquor stores, adult-oriented entertainment facilities “strip clubs”, poker rooms, bail bonds, night clubs/salons/taverns, bingo halls, race tracks, gaming establishments, gun/ammunition stores, cruise ships, psychic readers, smoking shops, tattoo/piercing shops, and spa/massage salons is strictly prohibited and will result in a loss of TANF benefits for six months for the first violation, twelve months for the second violation and permanently for the third violation.</li>
                            <li class="mt-3">If a court of law finds you or any household member hiding information or you do not report changes on time or do not tell the truth and are convicted, you may not get TANF for 6 months for the first violation, 12 months for the second violation and permanently for the third violation.</li>
                            <li class="mt-3">If a court of law finds you or any household member guilty of giving false information about where you live so you can receive benefits in more than one state, you will be barred for 10 years.</li>
                            <li class="mt-3">If a court convicted you of a drug-related charge controlled substance or a serious violent felony, on or after 1/1/97 you or that household member will not be eligible and/or permanently disqualified.</li>
                        </ul>
                    </div>

                    <SaveButton v-if="!moduleState.miscState.tanfWaiver.confirmed" class="mt-5" icon="fa-square" label="Click To Agree" large @click="moduleState.miscState.tanfWaiver.confirmed = true"/>
                    <SaveButton v-else class="mt-5" icon="fa-check-square" label="I Agree" large @click="moduleState.miscState.tanfWaiver.confirmed = false"/>
                </div>
            </div>

            <!-- Page: General waiver -->
            <div v-if="form.state.activePage == 'generalWaiver'">
                <div class="termsBox">
                    <h2 class="termsHeading">You must read and agree to the following declaration to continue</h2>

                    <div class="termsContent">
                        <p>I declare under penalty of perjury to the best of my knowledge and belief that the person(s) for whom I am applying for benefits is/are U.S. citizen(s) or are noncitizen(s) lawfully present in the United States. I further certify that all of the information provided on this application is true and correct to the best of my knowledge.</p>
                        <p class="mt-3">I understand and agree that DHS-DFCS, DCH and authorized Federal Agencies may verify the information I give on this application. Information may be obtained from past or present employers. I understand that my information will be used to track wage information and my participation in work activities.</p>
                        <p class="mt-3">I will report any change in my situation according to Food Stamp/Medicaid and/or TANF program requirements. I will also report If anyone in my household receives lottery or gambling winnings, gross amount of $3500 or more (before taxes or other amounts are withheld). I will report these winnings within 10 days from the end of the month in which my household receives the winnings.</p>
                        <p class="mt-3">I understand if any information is incorrect, my benefits may be reduced or denied, and I may be subject to criminal prosecution or disqualified from DHS-DFCS programs for knowingly providing incorrect information. I understand that I can be prosecuted if I provide false information or hide information.</p>
                        <p class="mt-3">I understand that if I fail to tell DHS-DFCS about some of my expenses at my application or renewal interview and/or fail to verify them that DHS-DFCS will not budget that expense in calculating the amount of my food stamp benefits.</p>
                    </div>

                    <SaveButton v-if="!moduleState.miscState.generalWaiver.confirmed" class="mt-5" icon="fa-square" label="Click To Agree" large @click="moduleState.miscState.generalWaiver.confirmed = true"/>
                    <SaveButton v-else class="mt-5" icon="fa-check-square" label="I Agree" large @click="moduleState.miscState.generalWaiver.confirmed = false"/>
                </div>
            </div>

            <!-- Page: Authorized representatives -->
            <div v-if="form.state.activePage == 'authorizedRepresentative' && moduleState.application.authorizedRepresentatives">
                <h2 class="pageHeading">Do you have somebody who will act as your authorized representative?</h2>
                
                <p>They will need to be with you to sign this application.</p>
                <p class="mt-2">They will be allowed to help you fill out your application, or complete eligibility interviews on your behalf.</p>
                <p v-if="moduleState.application.selectedBenefits!.foodStamps" class="mt-2">They will also be able to use your EBT (Food Stamps) card to buy food when you cannot go to the store yourself.</p>

                <AuthorizedRepresentativeEditor ref="authorizedRep1Field" prompt="Would you like to add an authorized representative? *" v-model="moduleState.application.authorizedRepresentatives.first"/>
            </div>

            <div v-if="form.state.activePage == 'authorizedRepresentative2' && moduleState.application.authorizedRepresentatives">
                <h2 class="pageHeading">Do you have another authorized representative?</h2>                
                <AuthorizedRepresentativeEditor ref="authorizedRep2Field" prompt="Would you like to add a second authorized representative? *" v-model="moduleState.application.authorizedRepresentatives.second"/>
            </div>

            <div v-if="form.state.activePage == 'authorizedRepresentativeSettings' && moduleState.application.authorizedRepresentatives">
                <h2 class="pageHeading">About your representatives</h2>                
                <FormRadioGroupField label="Would you like your representative(s) to have a copy of your Medicaid card? *" :options="{'Yes': true, 'No': false}" v-model="moduleState.application.authorizedRepresentatives.toReceiveMedicaidCard"/>

                <RequiredAssistanceEditor
                    requiredLabel="My representative(s) require additional assistance (sign language, braille, etc)"
                    whichTypesLabel="Please indicate which assistance they require"
                    frequencyLabel="Please briefly describe when they require this assistance"
                    v-model="moduleState.application.authorizedRepresentatives.requiredAssistance" />
            </div>

            <!-- Page: Basic Citizen Info -->
            <div v-if="form.state.activePage == 'basicCitizenInfo' && moduleState.application.applicant">
                <h2 class="pageHeading">Please provide some basic information about yourself</h2>

                <Columns>
                    <FormTextField label="First Name *" v-model="moduleState.application.applicant.firstName" trimmed autoFocus/>
                    <FormTextField label="Middle Initial" v-model="moduleState.application.applicant.middleInitial" trimmed :length="1"/>
                    <FormTextField label="Last Name *" v-model="moduleState.application.applicant.lastName" trimmed/>
                    <FormTextField label="Suffix" v-model="moduleState.application.applicant.suffix" trimmed/>
                </Columns>
                
                <FormTextField label="Social Security Number *" v-model="moduleState.application.applicant.socialSecurityNumber" trimmed :length="9"/>
                <FormRadioGroupField label="Are you a US citizen, or qualified immigrant? *" :options="{'Yes': true, 'No': false}" v-model="moduleState.application.applicant.isUSCitizenOrQualifiedImmigrant"/>
                <div style="margin-top: -0.5em; margin-bottom:1.5em"><em>Not being a US citizen does not disqualify you from benefits</em></div>

                <HalfColumn>
                    <FormSelectField label="Your Sex / Gender *" :options="ProfileTypes.Sex" allowNullSelection v-model="moduleState.application.applicant.sex"/>
                </HalfColumn>

                <Columns>
                    <FormDateField label="Date Of Birth *" v-model="moduleState.application.applicant.dateOfBirth" past/>
                    
                    <div class="column" style="padding-top: 2.75rem">
                        <span v-if="moduleState.application.applicant.dateOfBirth">
                            You are <strong>{{ formatAge(moduleState.application.applicant.dateOfBirth as Date) }}</strong> old
                        </span>
                    </div>
                </Columns>
            </div>

            <!-- Page: Address info -->
            <div v-if="form.state.activePage == 'address' && moduleState.application.applicant">
                <h2 class="pageHeading">Please provide your address</h2>

                <FormCheckboxField :boxLabel="`I have no fixed address - use ${sessionState.session!.user.assignedCareProvider!.name} address instead`" v-model="moduleState.application.applicant.useCareProviderAddressForMailing"/>

                <div v-if="!moduleState.application.applicant.useCareProviderAddressForMailing">
                    <Columns>
                        <FormTextField label="Building Number / Name *" v-model="moduleState.application.applicant.mailingAddress!.number" autoFocus />
                        <FormTextField label="Street *" v-model="moduleState.application.applicant.mailingAddress!.street" trimmed />
                    </Columns>

                    <Columns>
                        <FormTextField label="City *" v-model="moduleState.application.applicant.mailingAddress!.city" trimmed />
                        <FormTextField label="Zip Code *" v-model="moduleState.application.applicant.mailingAddress!.postalCode" numeric :length="9" />
                        <FormSelectField label="State *" :options="ProfileTypes.StateNotUnknown" allowNullSelection v-model="moduleState.application.applicant.mailingAddress!.state" />
                    </Columns>
                </div>
            </div>

            <!-- Page: Contact info -->
            <div v-if="form.state.activePage == 'contactInfo' && moduleState.application.applicant">
                <h2 class="pageHeading">Please let us know how the benefits office can reach you</h2>

                <Columns>
                    <FormTextField label="Main Phone Number" v-model="moduleState.application.applicant.mainPhone" autoFocus trimmed />
                    <FormTextField label="Alternate Phone Number" v-model="moduleState.application.applicant.otherPhone" trimmed />
                </Columns>
                
                <FormCheckboxField boxLabel="I'm happy for the benefits office to contact me via SMS" v-model="moduleState.application.applicant.smsCommunicationPreferred"/>

                <HalfColumn class="mt-4">
                    <FormTextField label="Email Address" v-model="moduleState.application.applicant.email" trimmed />
                </HalfColumn>
                
                <FormCheckboxField boxLabel="I'm happy for the benefits office to contact me via email" v-model="moduleState.application.applicant.emailCommunicationPreferred"/>
            </div>

            <!-- Page: Communication preferences -->
            <div v-if="form.state.activePage == 'communicationPreferences' && moduleState.application.applicant">
                <h2 class="pageHeading">Do you require any additional assistance?</h2>

                <Columns>
                    <FormTextField label="Preferred language *" v-model="moduleState.application.applicant.preferredLanguage" autoFocus/>
                    <FormCheckboxField class="mt-5" boxLabel="I need an interpreter" v-model="moduleState.application.applicant.interpreterNeeded"/>
                </Columns>

                <RequiredAssistanceEditor
                    requiredLabel="I require additional assistance (sign language, braille, etc)"
                    whichTypesLabel="Please indicate which assistance you require"
                    frequencyLabel="Please briefly describe when you require this assistance"
                    v-model="moduleState.application.applicant.requiredAssistance" />
            </div>

            <!-- Page: Household members -->
            <div v-if="form.state.activePage == 'householdMembers' && moduleState.application.householdMembers">
                <h2 class="pageHeading">Please tell us about all members of your household</h2>

                <ListEditor class="mt-3" headerLabel="Household Members"
                        :items="moduleState.application.householdMembers"
                        :fixedItemLabel="`${formatName(moduleState.application.applicant!.firstName!, moduleState.application.applicant!.middleInitial, moduleState.application.applicant!.lastName!)} (applicant)`"
                        itemIcon="fa-user" :itemLabel="(item)=>formatName(item.firstName!, item.middleInitial, item.lastName!)"
                        addLabel="Add" :onAdd="()=>householdMembers.add()"
                        :onEdit="(idx)=>householdMembers.edit(idx)" :onRemove="(idx)=>householdMembers.remove(idx)" />

                <ModalDialog
                  v-if="householdMembers.activeItem" active
                  title="Household Member" severity="link"
                  firstButtonText="Cancel" firstButtonSeverity="danger" @firstbuttonselected="householdMembers.activeItem = null"
                  secondButtonText="Save"  secondButtonSeverity="link"  @secondbuttonselected="householdMembers.onEditComplete()">

                    <div v-for="(item, idx) in [householdMembers.activeItem]" :key="idx /* Defining 'item' alias */" class="modalScrollContainer">

                        <Columns>
                            <FormTextField label="First Name *" v-model="item.firstName" trimmed autoFocus/>
                            <FormTextField label="Middle Initial" v-model="item.middleInitial" trimmed :length="1"/>
                            <FormTextField label="Last Name *" v-model="item.lastName" trimmed/>
                        </Columns>

                        <Columns>
                            <FormDateField label="Date Of Birth *" v-model="item.dateOfBirth" past/>
                            
                            <div class="column" style="padding-top: 2rem">
                                <span v-if="item.dateOfBirth">
                                    This household member is <strong>{{ formatAge(item.dateOfBirth as Date) }}</strong> old
                                </span>
                            </div>
                        </Columns>

                        <Columns>
                            <FormSelectField label="Sex / Gender *" :options="ProfileTypes.Sex" allowNullSelection v-model="item.sex"/>
                            <FormSelectField label="Race / Ethnicity *" :options="GeorgiaDfcsBenefits.Race" allowNullSelection v-model="item.race"/>
                        </Columns>

                        <HalfColumn>
                            <FormSelectField label="What is this person's relationship to you? *" :options="GeorgiaDfcsBenefits.Relationship" allowNullSelection v-model="item.relationship"/>
                        </HalfColumn>

                        <FormRadioGroupField label="Is this person applying for benefits? *" :options="{'Yes': true, 'No': false}" v-model="item.isApplyingForBenefits"/>

                        <HalfColumn v-if="item.isApplyingForBenefits">
                            <FormTextField label="Social Security Number *" v-model="item.socialSecurityNumber" trimmed :length="9"/>
                        </HalfColumn>

                        <FormRadioGroupField v-if="item.isApplyingForBenefits" label="Is this household member a US citizen, or qualified immigrant? *" :options="{'Yes': true, 'No': false}" v-model="item.isUSCitizenOrQualifiedImmigrant"/>
                        
                        <div v-if="householdMembers.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                            <p v-for="error in householdMembers.errors" :key="error">{{ error }}</p>
                        </div>
                        <div v-else class="mt-6"></div>
                    </div>
                </ModalDialog>
            </div>

            <!-- Page: Food Stamps qualification questions -->
            <div v-if="form.state.activePage == 'foodStampsQualificationQuestions'">
                <h2 class="pageHeading">These questions will help speed up your Food Stamps application</h2>

                <div v-for="(qs, idx) in [moduleState!.application.expeditedFoodStampQualification!]" :key="idx /* Defining 'qs' alias */">
                    <FormRadioGroupField label="Are you, or any household members, migrant or seasonal farm workers? *" :options="{'Yes': true, 'No': false}" v-model="qs.isMigrantOrSeasonalFarmWorker"/>
                    <HalfColumn v-if="qs.isMigrantOrSeasonalFarmWorker">
                        <FormTextField label="Who? *" v-model="qs.whoMigrantOrSeasonalFarmWorker" trimmed/>
                    </HalfColumn>
                    
                    <hr class="questionDivider">
                    <FormTextField label="What is your total earned income expected for this month (from employment - before tax) ($)? *" v-model="qs.totalEarnedIncomeThisMonth" numeric/>
                    <div v-if="qs.totalEarnedIncomeThisMonth! > 0">
                        <Columns>
                            <FormTextField label="Employer name *" v-model="qs.employerName" trimmed/>
                            <FormTextField label="How many hours do you work per week? *" v-model="qs.employmentHoursPerWeek" numeric />
                        </Columns>
                        <Columns>
                            <FormTextField label="Employment begin date *" v-model="qs.employmentBeginDate" trimmed/>
                            <FormTextField label="Employment end date *" v-model="qs.employmentEndDate" trimmed/>
                        </Columns>
                        <Columns>
                            <FormTextField label="Usual payment amount ($) *" v-model="qs.employmentPayRate" numeric />
                            <FormSelectField label="Payment frequency *" :options="GeorgiaDfcsBenefits.PaymentFrequency" v-model="qs.earnedIncomeFrequency"/>
                        </Columns>
                    </div>

                    <hr class="questionDivider">
                    <FormTextField label="What is your total unearned income expected for this month (income from other sources, such as welfare payments, child support, inheritance, or pension - before tax) ($)? *" v-model="qs.totalUnearnedIncomeThisMonth" numeric/>
                    <div v-if="qs.totalUnearnedIncomeThisMonth && qs.totalUnearnedIncomeThisMonth > 0">
                        <HalfColumn>
                            <FormTextField label="Income type / source *" v-model="qs.firstUnearnedIncomeType" trimmed/>
                        </HalfColumn>
                        <Columns>
                            <FormTextField label="Usual payment amount ($) *" v-model="qs.firstUnearnedIncomeAmount" numeric/>
                            <FormSelectField label="Payment frequency *" :options="GeorgiaDfcsBenefits.PaymentFrequency" v-model="qs.firstUnearnedIncomeFrequency"/>
                        </Columns>

                        <HalfColumn>
                            <FormCheckboxField boxLabel="I have another source of unearned income" v-model="moduleState.miscState.foodStampsQualificationQuestions.hasSecondUnearnedIncome"/>
                        </HalfColumn>

                        <div v-if="moduleState.miscState.foodStampsQualificationQuestions.hasSecondUnearnedIncome">
                            <HalfColumn>
                                <FormTextField label="Income type / source *" v-model="qs.secondUnearnedIncomeType" trimmed/>
                            </HalfColumn>
                            <Columns>
                                <FormTextField label="Usual payment amount ($) *" v-model="qs.secondUnearnedIncomeAmount" numeric/>
                                <FormSelectField label="Payment frequency *" :options="GeorgiaDfcsBenefits.PaymentFrequency" v-model="qs.secondUnearnedIncomeFrequency"/>
                            </Columns>
                        </div>
                    </div>

                    <hr class="questionDivider">
                    <FormTextField label="What is your total earned and unearned income expected for this month (income from all sources, before tax) ($)? *" v-model="qs.totalIncomeThisMonth" numeric/>
                    <FormTextField label="How much money do you and all household members have in cash or in the bank ($)? *" v-model="qs.totalMoneyInCashAndBank" numeric/>

                    <hr class="questionDivider">
                    <FormTextField label="What is the monthly total you pay for rent, mortgage, property taxes, and/or homeowners insurance ($)? *" v-model="qs.totalRentOrMortgagePayment" numeric/>
                    <FormTextField label="What is the monthly total you pay for electric, water, gas, and/or other utilities (exclude any late fees or other repayments) ($)? *" v-model="qs.totalUtilitiesPayment" numeric/>
                    
                    <FormCheckboxField boxLabel="Our household uses electric for heating" v-model="qs.hasElectricUtilities" />
                    <FormCheckboxField boxLabel="Our household uses gas for heating" v-model="qs.hasGasUtilities" />
                    <FormCheckboxField boxLabel="Our household uses window or central air conditioning" v-model="qs.hasAirConUtilities" />
                    <FormCheckboxField boxLabel="Our household uses kerosene oil heating" v-model="qs.hasKeroseneUtilities" />
                    <FormCheckboxField boxLabel="Our household uses wood for heating" v-model="qs.hasWoodUtilities" />

                    <FormRadioGroupField class="mt-3" label="Have you received energy assistance in the last 12 months? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasReceivedEnergyAssistance"/>
                    <HalfColumn v-if="qs.hasReceivedEnergyAssistance">
                        <FormTextField label="What amount did you receive ($)? *" v-model="qs.energyAssistanceAmount" numeric/>
                    </HalfColumn>
                </div>
            </div>

            <!-- Page: Additional qualification questions -->
            <div v-if="form.state.activePage == 'additionalQualificationQuestions'">
                <h2 class="pageHeading">Please answer these additional questions about yourself and your household members. Your answers may affect the benefits you are eligible for</h2>

                <div v-for="(qs, idx) in [moduleState!.application.additionalQualifications!]" :key="idx /* Defining 'qs' alias */">
                    <FormRadioGroupField label="Has anyone received any benefits in another county or state? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasBenefitsFromOtherCountyOrState"/>
                    <Columns v-if="qs.hasBenefitsFromOtherCountyOrState">
                        <FormTextField label="Who? *" v-model="qs.whoBenefitsFromOtherCountyOrState" trimmed/>
                        <FormTextField label="Where? *" v-model="qs.whereBenefitsFromOtherCountyOrState" trimmed/>
                        <FormTextField label="When? *" v-model="qs.whenBenefitsFromOtherCountyOrState" trimmed/>
                    </Columns>

                    <div v-if="moduleState!.application.selectedBenefits!.foodStamps">
                        <hr class="questionDivider">
                        <FormRadioGroupField label="Has anyone been convicted of giving false information about where they live or who they are to get multiple Food Stamps benefits in more than one area after 8/22/96? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasFalseInformationBenefitsConvicion"/>
                        <Columns v-if="qs.hasFalseInformationBenefitsConvicion">
                            <FormTextField label="Who? *" v-model="qs.whoFalseInformationBenefitsConvicion" trimmed/>
                            <FormTextField label="Where? *" v-model="qs.whereFalseInformationBenefitsConvicion" trimmed/>
                            <FormTextField label="When? *" v-model="qs.whenFalseInformationBenefitsConvicion" trimmed/>
                        </Columns>

                        <hr class="questionDivider">
                        <FormRadioGroupField label="Has anyone been convicted of trading Food Stamp benefits for drugs after 8/22/96? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasFoodStampDrugTradingConviction"/>
                        <Columns v-if="qs.hasFoodStampDrugTradingConviction">
                            <FormTextField label="Who? *" v-model="qs.whoFoodStampDrugTradingConviction" trimmed/>
                            <FormTextField label="When? *" v-model="qs.whenFoodStampDrugTradingConviction" trimmed/>
                        </Columns>
                        
                        <hr class="questionDivider">
                        <FormRadioGroupField label="Has anyone been convicted of buying or selling Food Stamp benefits over $500 after 8/22/96? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasFoodStampSellingConviction"/>
                        <Columns v-if="qs.hasFoodStampSellingConviction">
                            <FormTextField label="Who? *" v-model="qs.whoFoodStampSellingConviction" trimmed/>
                            <FormTextField label="When? *" v-model="qs.whenFoodStampSellingConviction" trimmed/>
                        </Columns>

                        <hr class="questionDivider">
                        <FormRadioGroupField label="Have you or any household member been convicted of trading Food Stamp benefits for guns, ammunition or explosives after 8/22/96? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasFoodStampWeaponsTradingConviction"/>
                        <Columns v-if="qs.hasFoodStampWeaponsTradingConviction">
                            <FormTextField label="Who? *" v-model="qs.whoFoodStampWeaponsTradingConviction" trimmed/>
                            <FormTextField label="When? *" v-model="qs.whenFoodStampWeaponsTradingConviction" trimmed/>
                        </Columns>

                        <hr class="questionDivider">
                        <FormRadioGroupField label="Has anyone received lottery or gambling winnings? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasGamblingWinnings"/>
                        <Columns v-if="qs.hasGamblingWinnings">
                            <FormTextField label="Who? *" v-model="qs.whoGamblingWinnings" trimmed/>
                            <FormTextField label="When? *" v-model="qs.whenGamblingWinnings" trimmed/>
                            <FormTextField label="Amount received *" v-model="qs.gamblingWinningsAmount" trimmed/>
                        </Columns>
                    </div>

                    <div v-if="moduleState!.application.selectedBenefits!.foodStamps || moduleState!.application.selectedBenefits!.TANF">
                        <hr class="questionDivider">
                        <FormRadioGroupField label="Did anyone in your household voluntarily quit a job, or voluntarily reduce their work hours below 30 hours per week, within the last 30 days? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasVoluntarilyQuitOrReducedHours"/>
                        <Columns v-if="qs.hasVoluntarilyQuitOrReducedHours">
                            <FormTextField label="Who? *" v-model="qs.whoVoluntarilyQuitOrReducedHours" trimmed/>
                            <FormTextField label="Why? *" v-model="qs.whyVoluntarilyQuitOrReducedHours" trimmed/>
                        </Columns>

                        <hr class="questionDivider">
                        <FormRadioGroupField label="Is anyone disqualified from the Food Stamp or TANF Program? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasFoodStampOrTANFDisqualification"/>
                        <Columns v-if="qs.hasFoodStampOrTANFDisqualification">
                            <FormTextField label="Who? *" v-model="qs.whoFoodStampOrTANFDisqualification" trimmed/>
                            <FormTextField label="Where? *" v-model="qs.whereFoodStampOrTANFDisqualification" trimmed/>
                        </Columns>
                        
                        <hr class="questionDivider">
                        <FormRadioGroupField label="Is anyone trying to avoid prosecution or jail for a felony? *" :options="{'Yes': true, 'No': false}" v-model="qs.isAvoidingProsecutionOrJail"/>
                        <HalfColumn v-if="qs.isAvoidingProsecutionOrJail">
                            <FormTextField label="Who? *" v-model="qs.whoAvoidingProsecutionOrJail" trimmed/>
                        </HalfColumn>

                        <hr class="questionDivider">
                        <FormRadioGroupField label="Is anyone violating conditions of probation or parole? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasParoleViolation"/>
                        <HalfColumn v-if="qs.hasParoleViolation">
                            <FormTextField label="Who? *" v-model="qs.whoParoleVoliation" trimmed/>
                        </HalfColumn>

                        <hr class="questionDivider">
                        <FormRadioGroupField label="Does anyone have a violent or drug-related felony conviction after 8/22/96? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasFelonyDrugOrViolenceConviction"/>
                        <div v-if="qs.hasFelonyDrugOrViolenceConviction">
                            <Columns>
                                <FormTextField label="Who? *" v-model="qs.whoFelonyDrugOrViolenceConviction" trimmed/>
                                <FormTextField label="When? *" v-model="qs.whenFelonyDrugOrViolenceConviction" trimmed/>
                            </Columns>
                        
                            <FormRadioGroupField label="Are you in compliance with all probation terms for any drug-related felony? *" :options="{'Yes': true, 'No': false}" v-model="qs.isComplyingWithFelonyDrugOrViolenceConvictionProbationTerms"/>
                            <FormRadioGroupField label="Are you in compliance with all parole terms for any drug-related felony? *" :options="{'Yes': true, 'No': false}" v-model="qs.isComplyingWithFelonyDrugOrViolenceConvictionParoleTerms"/>
                            <FormRadioGroupField label="Have you successfully completed ALL terms of probation or parole for any drug-related conviction? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasCompletedAllFelonyDrugOrViolenceConvictionTerms"/>
                        </div>
                    </div>

                    <div v-if="moduleState!.application.selectedBenefits!.medicaid">
                        <hr class="questionDivider">
                        <FormRadioGroupField label="Does anyone have any unpaid medical bills for the last 3 months? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasUnpaidMedicalBills"/>
                    </div>

                    <div v-if="moduleState!.application.selectedBenefits!.medicaid || moduleState!.application.selectedBenefits!.TANF">
                        <hr class="questionDivider">
                        <FormRadioGroupField label="Is anyone pregnant? *" :options="{'Yes': true, 'No': false}" v-model="qs.isPregnant"/>
                        <Columns v-if="qs.isPregnant">
                            <FormTextField label="Who? *" v-model="qs.whoPregnant" trimmed/>
                            <FormTextField label="What is their due date? *" v-model="qs.pregnancyDueDate" trimmed/>
                        </Columns>
                    </div>

                    <div v-if="moduleState!.application.selectedBenefits!.TANF">
                        <hr class="questionDivider">
                        <FormRadioGroupField label="Has anyone used TANF funds or the EPPIC Card at liquor stores, casinos, poker rooms, adult entertainment business, bail bonds, night clubs, salons/taverns, bingo halls, race tracks, gun/ammunition stores, cruise ships, psychic readers, smoking shops, tattoo/piercing shops or spa/massage salons? *" :options="{'Yes': true, 'No': false}" v-model="qs.hasMisusedTANF"/>
                        <Columns v-if="qs.hasMisusedTANF">
                            <FormTextField label="Who? *" v-model="qs.whoMisusedTANF" trimmed/>
                            <FormTextField label="When? *" v-model="qs.whenMisusedTANF" trimmed/>
                        </Columns>
                    </div>
                    
                </div>
            </div>

            <!-- Page: Income -->
            <div v-if="form.state.activePage == 'income' && moduleState.application.income">
                <h2 class="pageHeading">Please tell us about your household income</h2>

                <p>Please add information for <strong>ALL</strong> household income.</p>
                <p>This includes wages, tips, bonuses, self-employment, pensions, child support, disability, etc.</p>

                <ListEditor class="mt-3" headerLabel="Income Sources"
                        :items="moduleState.application.income"
                        itemIcon="fa-coins" :itemLabel="(item)=>`$${item.monthlyAmount}/mo from ${item.source} to ${item.name}`"
                        addLabel="Add" :onAdd="()=>incomeSources.add()"
                        :onEdit="(idx)=>incomeSources.edit(idx)" :onRemove="(idx)=>incomeSources.remove(idx)" />

                <ModalDialog
                  v-if="incomeSources.activeItem" active
                  title="Income Source" severity="link"
                  firstButtonText="Cancel" firstButtonSeverity="danger" @firstbuttonselected="incomeSources.activeItem = null"
                  secondButtonText="Save"  secondButtonSeverity="link"  @secondbuttonselected="incomeSources.onEditComplete()">

                    <div v-for="(item, idx) in [incomeSources.activeItem]" :key="idx /* Defining 'item' alias */" class="modalScrollContainer">

                        <Columns>
                            <FormTextField label="Who receives it? *" v-model="item.name" trimmed autoFocus/>
                            <FormTextField label="Monthly amount ($, before deductions) *" v-model="item.monthlyAmount" numeric/>
                        </Columns>

                        <Columns>
                            <FormTextField label="Type of income *" placeholder="e.g. wages, self-employment, benefits" v-model="item.type" trimmed/>
                            <FormTextField label="Income source *" placeholder="e.g. employer name, type of benefit" v-model="item.source" trimmed/>
                        </Columns>

                        <Columns>
                            <FormTextField label="Hourly rate ($)" v-model="item.hourlyRate" numeric/>
                            <FormTextField label="Hours per week" v-model="item.hoursPerWeek" numeric/>
                        </Columns>

                        <Columns>
                            <FormSelectField label="Payment frequency *" :options="GeorgiaDfcsBenefits.PaymentFrequency" v-model="item.frequency"/>
                            <FormTextField label="Dates paid *" v-model="item.datesPaid" trimmed/>
                        </Columns>

                        <div v-if="incomeSources.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                            <p v-for="error in incomeSources.errors" :key="error">{{ error }}</p>
                        </div>
                        <div v-else class="mt-6"></div>
                    </div>
                </ModalDialog>
            </div>

            <!-- Page: Care Expenses -->
            <div v-if="form.state.activePage == 'careExpenses' && moduleState.application.expenses?.careExpenses">
                <h2 class="pageSubHeading">Do you pay for the care of any dependent child or disabled adult household members?</h2>

                <FormRadioGroupField :options="{'Yes': true, 'No': false}" v-model="moduleState.miscState.expenses.hasCareExpenses"/>

                <div v-if="moduleState.miscState.expenses.hasCareExpenses">
                    <h2 class="pageSubHeading">Please list these all of these expenses</h2>
                
                    <ListEditor class="mt-3"
                        :items="moduleState.application.expenses.careExpenses"
                        itemIcon="fa-coins" :itemLabel="(item)=>`$${item.amount} for ${item.reason} to ${item.recipientName}`"
                        addLabel="Add" :onAdd="()=>careExpenses.add()"
                        :onEdit="(idx)=>careExpenses.edit(idx)" :onRemove="(idx)=>careExpenses.remove(idx)" />
                </div>

                <ModalDialog
                  v-if="careExpenses.activeItem" active
                  title="Care Expense" severity="link"
                  firstButtonText="Cancel" firstButtonSeverity="danger" @firstbuttonselected="careExpenses.activeItem = null"
                  secondButtonText="Save"  secondButtonSeverity="link"  @secondbuttonselected="careExpenses.onEditComplete()">

                    <div v-for="(item, idx) in [careExpenses.activeItem]" :key="idx /* Defining 'item' alias */" class="modalScrollContainer">

                        <Columns>
                            <FormTextField label="Who requires care? *" v-model="item.recipientName" trimmed autoFocus/>
                            <FormTextField label="Who pays for the care? *" v-model="item.payerName" trimmed/>
                        </Columns>

                        <Columns>
                            <FormTextField label="Reason / type of care *" v-model="item.reason" trimmed/>
                            <FormTextField label="Provider's name / number *" v-model="item.provider" trimmed/>
                        </Columns>

                        <Columns>
                            <FormTextField label="Amount paid ($) *" v-model="item.amount" numeric/>
                            <FormSelectField label="Payment frequency *" :options="GeorgiaDfcsBenefits.PaymentFrequency" v-model="item.frequency"/>
                        </Columns>

                        <div v-if="careExpenses.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                            <p v-for="error in careExpenses.errors" :key="error">{{ error }}</p>
                        </div>
                        <div v-else class="mt-6"></div>
                    </div>
                </ModalDialog>
            </div>

            <!-- Page: Dependent Transportation Expenses -->
            <div v-if="form.state.activePage == 'dependentTransportationExpenses' && moduleState.application.expenses">
                <h2 class="pageSubHeading">Do you pay transport expenses for a dependent child or disabled adult household member?</h2>

                <FormRadioGroupField :options="{'Yes': true, 'No': false}" v-model="moduleState.application.expenses.hasDependentTransportationExpenses"/>

                <div v-if="moduleState.application.expenses.hasDependentTransportationExpenses">
                    <FormRadioGroupField v-if="moduleState.miscState.expenses.hasCareExpenses" label="Did you already include these expenses on the previous screen? *" :options="{'Yes': true, 'No': false}" v-model="moduleState.application.expenses.areDependentTransportationExpensesIncluded"/>
                    <FormTextField v-if="!moduleState.miscState.expenses.hasCareExpenses || moduleState.application.expenses.areDependentTransportationExpensesIncluded == false" label="What is the total number of miles driven weekly? *" v-model="moduleState.application.expenses.totalMilesDrivenWeekly" numeric/>
                </div>
            </div>

            <!-- Page: Medical Expenses -->
            <div v-if="form.state.activePage == 'medicalExpenses' && moduleState.application.expenses?.medicalExpenses">
                <h2 class="pageSubHeading">Does anyone in your household who is 60+ years of age, or is disabled, have any medical expenses?</h2>
                <p><strong>This DOES NOT include transport expenses.</strong></p>

                <FormRadioGroupField :options="{'Yes': true, 'No': false}" v-model="moduleState.miscState.expenses.hasMedicalExpenses"/>

                <div v-if="moduleState.miscState.expenses.hasMedicalExpenses">
                    <h2 class="pageSubHeading">Please list these all of these expenses</h2>

                    <ListEditor class="mt-3"
                        :items="moduleState.application.expenses.medicalExpenses"
                        itemIcon="fa-coins" :itemLabel="(item)=>`$${item.amount} for ${item.type} to ${item.name}`"
                        addLabel="Add" :onAdd="()=>medicalExpenses.add()"
                        :onEdit="(idx)=>medicalExpenses.edit(idx)" :onRemove="(idx)=>medicalExpenses.remove(idx)" />
                </div>

                <ModalDialog
                  v-if="medicalExpenses.activeItem" active
                  title="Medical Expense" severity="link"
                  firstButtonText="Cancel" firstButtonSeverity="danger" @firstbuttonselected="medicalExpenses.activeItem = null"
                  secondButtonText="Save"  secondButtonSeverity="link"  @secondbuttonselected="medicalExpenses.onEditComplete()">

                    <div v-for="(item, idx) in [medicalExpenses.activeItem]" :key="idx /* Defining 'item' alias */" class="modalScrollContainer">

                        <Columns>
                            <FormTextField label="Who has this expense? *" v-model="item.name" trimmed autoFocus/>
                            <FormTextField label="Type of expense (e.g. doctor visits, hospital visits, prescriptions...) *" v-model="item.type" trimmed/>
                        </Columns>

                        <Columns>
                            <FormTextField label="Amount ($) *" v-model="item.amount" numeric/>
                            <FormTextField label="Date paid (if applicable)" v-model="item.datePaid" trimmed/>
                        </Columns>

                        <FormRadioGroupField label="Is this money still owed? *" :options="{'Yes': true, 'No': false}" v-model="item.stillOwed"/>
                        <FormRadioGroupField label="Is this expense covered by your insurance? *" :options="{'Yes': true, 'No': false}" v-model="item.willInsurancePay"/>

                        <div v-if="medicalExpenses.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                            <p v-for="error in medicalExpenses.errors" :key="error">{{ error }}</p>
                        </div>
                        <div v-else class="mt-6"></div>
                    </div>
                </ModalDialog>
            </div>

            <!-- Page: Medical Transportation Expenses -->
            <div v-if="form.state.activePage == 'medicalTransportationExpenses' && moduleState.application.expenses?.medicalTransportationExpenses">
                <h2 class="pageSubHeading">Does anyone in your household who is 60+ years of age, or is disabled, have any medical transportation expenses?</h2>

                <FormRadioGroupField :options="{'Yes': true, 'No': false}" v-model="moduleState.miscState.expenses.hasMedicalTransportationExpenses"/>

                <div v-if="moduleState.miscState.expenses.hasMedicalTransportationExpenses">
                    <h2 class="pageSubHeading">Please list these all of these expenses</h2>

                    <ListEditor class="mt-3"
                        :items="moduleState.application.expenses.medicalTransportationExpenses"
                        itemIcon="fa-coins" :itemLabel="(item)=>`$${item.cost} for ${item.purpose}`"
                        addLabel="Add" :onAdd="()=>medicalTransportationExpenses.add()"
                        :onEdit="(idx)=>medicalTransportationExpenses.edit(idx)" :onRemove="(idx)=>medicalTransportationExpenses.remove(idx)" />
                </div>

                <ModalDialog
                  v-if="medicalTransportationExpenses.activeItem" active
                  title="Medical Transportation Expense" severity="link"
                  firstButtonText="Cancel" firstButtonSeverity="danger" @firstbuttonselected="medicalTransportationExpenses.activeItem = null"
                  secondButtonText="Save"  secondButtonSeverity="link"  @secondbuttonselected="medicalTransportationExpenses.onEditComplete()">

                    <div v-for="(item, idx) in [medicalTransportationExpenses.activeItem]" :key="idx /* Defining 'item' alias */" class="modalScrollContainer">

                        <FormTextField label="Purpose of the trip (e.g. doctor visit, hospital visit, pharmacy pick-up...) *" v-model="item.purpose" trimmed autoFocus/>

                        <Columns>
                            <FormTextField label="Total miles travelled * " v-model="item.milesDriven" numeric/>
                            <FormTextField label="Cost of transport, parking, lodging etc. ($) *" v-model="item.cost" numeric/>
                        </Columns>

                        <div v-if="medicalTransportationExpenses.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                            <p v-for="error in medicalTransportationExpenses.errors" :key="error">{{ error }}</p>
                        </div>
                        <div v-else class="mt-6"></div>
                    </div>
                </ModalDialog>
            </div>

            <!-- Page: Child Support Expenses -->
            <div v-if="form.state.activePage == 'childSupportExpenses' && moduleState.application.expenses?.childSupportExpenses">
                <h2 class="pageSubHeading">Does anyone in your household pay child support to someone living OUTSIDE of the home?</h2>

                <FormRadioGroupField :options="{'Yes': true, 'No': false}" v-model="moduleState.miscState.expenses.hasChildSupportExpenses"/>

                <div v-if="moduleState.miscState.expenses.hasChildSupportExpenses">
                    <h2 class="pageSubHeading">Please list these all of these expenses</h2>

                    <ListEditor class="mt-3"
                        :items="moduleState.application.expenses.childSupportExpenses"
                        itemIcon="fa-coins" :itemLabel="(item)=>`$${item.obligatedAmount} for ${item.childName} to ${item.recipientName}`"
                        addLabel="Add Child Support Expense" :onAdd="()=>childSupportExpenses.add()"
                        :onEdit="(idx)=>childSupportExpenses.edit(idx)" :onRemove="(idx)=>childSupportExpenses.remove(idx)" />
                </div>
                
                <ModalDialog
                  v-if="childSupportExpenses.activeItem" active
                  title="Child Support Expense" severity="link"
                  firstButtonText="Cancel" firstButtonSeverity="danger" @firstbuttonselected="childSupportExpenses.activeItem = null"
                  secondButtonText="Save"  secondButtonSeverity="link"  @secondbuttonselected="childSupportExpenses.onEditComplete()">

                    <div v-for="(item, idx) in [childSupportExpenses.activeItem]" :key="idx /* Defining 'item' alias */" class="modalScrollContainer">

                        <Columns>
                            <FormTextField label="What is the name of the child? *" v-model="item.childName" trimmed autoFocus/>
                            <FormTextField label="Who pays child support? *" v-model="item.payerName" trimmed/>
                        </Columns>

                        <Columns>
                            <FormTextField label="How much are they obligated to pay ($)? *" v-model="item.obligatedAmount" numeric/>
                            <FormTextField label="How much did they actually pay ($)? *" v-model="item.actualAmount" numeric/>
                        </Columns>

                        <HalfColumn>
                            <FormTextField label="Who receives this payment? *" v-model="item.recipientName" trimmed/>
                        </HalfColumn>

                        <div v-if="childSupportExpenses.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                            <p v-for="error in childSupportExpenses.errors" :key="error">{{ error }}</p>
                        </div>
                        <div v-else class="mt-6"></div>
                    </div>
                </ModalDialog>
            </div>

            <!-- Page: Shelter or Utility Expenses -->
            <div v-if="form.state.activePage == 'shelterOrUtilityExpenses' && moduleState.application.expenses?.shelterOrUtilityExpenses">
                <h2 class="pageSubHeading">Does anyone in your household pay for any shelter or home utility expenses?</h2>

                <FormRadioGroupField :options="{'Yes': true, 'No': false}" v-model="moduleState.miscState.expenses.hasShelterOrUtilityExpenses"/>

                <div v-if="moduleState.miscState.expenses.hasShelterOrUtilityExpenses">
                    <hr class="questionDivider">
                    <ShelterOrUtilityExpenseEditor prompt="Do you pay rent / mortgage? *" v-model="moduleState.application.expenses.shelterOrUtilityExpenses.rentOrMortgage" />
                    <hr class="questionDivider">
                    <ShelterOrUtilityExpenseEditor prompt="Do you pay property tax? *" v-model="moduleState.application.expenses.shelterOrUtilityExpenses.propertyTax" />
                    <hr class="questionDivider">
                    <ShelterOrUtilityExpenseEditor prompt="Do you pay property insurance? *" v-model="moduleState.application.expenses.shelterOrUtilityExpenses.propertyInsurance" />
                    <hr class="questionDivider">
                    <ShelterOrUtilityExpenseEditor prompt="Do you pay for electricity? *" v-model="moduleState.application.expenses.shelterOrUtilityExpenses.electricity" />
                    <hr class="questionDivider">
                    <ShelterOrUtilityExpenseEditor prompt="Do you pay for gas? *" v-model="moduleState.application.expenses.shelterOrUtilityExpenses.gas" />
                    <hr class="questionDivider">
                    <ShelterOrUtilityExpenseEditor prompt="Do you pay for garbage disposal? *" v-model="moduleState.application.expenses.shelterOrUtilityExpenses.garbage" />
                    <hr class="questionDivider">
                    <ShelterOrUtilityExpenseEditor prompt="Do you pay for telephone? *" v-model="moduleState.application.expenses.shelterOrUtilityExpenses.telephone" />
                    <hr class="questionDivider">
                    <ShelterOrUtilityExpenseEditor prompt="Do you have any other shelter or utility expenses? *" v-model="moduleState.application.expenses.shelterOrUtilityExpenses.other" />
                </div>
            </div>

             <!-- Page: Shared Household Expenses -->
             <div v-if="form.state.activePage == 'sharedExpenses' && moduleState.application.expenses?.sharedExpenses && moduleState.application.expenses?.sharedExpenses.landlordAddress">
                <h2 class="pageHeading">Do you share monthly household expenses with anyone in the home?</h2>

                <FormRadioGroupField :options="{'Yes': true, 'No': false}" v-model="moduleState.miscState.expenses.hasSharedExpenses"/>

                <div v-if="moduleState.miscState.expenses.hasSharedExpenses">
                    <FormTextField label="Who do you share expenses with? *" v-model="moduleState.application.expenses.sharedExpenses.who" trimmed autoFocus/>
                    <FormTextField label="Please describe the arrangement *" v-model="moduleState.application.expenses.sharedExpenses.comments" trimmed/>
                    <FormTextField label="Who do you pay to? *" v-model="moduleState.application.expenses.sharedExpenses.recipientName" trimmed/>

                    <Columns>
                        <FormTextField label="Amount paid ($) *" v-model="moduleState.application.expenses.sharedExpenses.paymentAmount" numeric/>
                        <FormSelectField label="Payment frequency *" :options="GeorgiaDfcsBenefits.PaymentFrequency" v-model="moduleState.application.expenses.sharedExpenses.paymentFrequency"/>
                    </Columns>

                    <FormTextField label="Landlord's name *" v-model="moduleState.application.expenses.sharedExpenses.landlordName" trimmed/>
                    <Columns>
                        <FormTextField label="Landlord's Building Number / Name" v-model="moduleState.application.expenses.sharedExpenses.landlordAddress!.number" />
                        <FormTextField label="Landlord's Street" v-model="moduleState.application.expenses.sharedExpenses.landlordAddress!.street" trimmed />
                    </Columns>
                    <Columns>
                        <FormTextField label="Landlord's City" v-model="moduleState.application.expenses.sharedExpenses.landlordAddress!.city" trimmed />
                        <FormSelectField label="Landlord's State" :options="ProfileTypes.StateNotUnknown" allowNullSelection v-model="moduleState.application.expenses.sharedExpenses.landlordAddress!.state" />
                    </Columns>
                </div>
            </div>

            <!-- Page: Other Bill Payer -->
            <div v-if="form.state.activePage == 'otherBillPayer' && moduleState.application.expenses?.otherBillPayer">
                <h2 class="pageHeading">Does someone else pay any of these household bills for you?</h2>

                <FormRadioGroupField :options="{'Yes': true, 'No': false}" v-model="moduleState.miscState.expenses.hasOtherBillPayer"/>

                <div v-if="moduleState.miscState.expenses.hasOtherBillPayer">
                    <FormTextField label="Who pays? *" v-model="moduleState.application.expenses.otherBillPayer.payerName" trimmed autoFocus/>
                    <FormTextField label="Which bills do they pay? *" v-model="moduleState.application.expenses.otherBillPayer.whichBills" trimmed/>
                    <FormTextField label="What amount is paid? *" v-model="moduleState.application.expenses.otherBillPayer.amount" trimmed/>
                    <FormTextField label="To whom does this person pay the bills? *" v-model="moduleState.application.expenses.otherBillPayer.recipientName" trimmed/>
                </div>
            </div>

            <!-- Page: ID upload -->
            <div v-if="form.state.activePage == 'supportDocGuidance'">
                <h2 class="pageHeading">Please upload photos / scans of any existing ID documents, and evidence of income and expenses, which you have available</h2>

                <SupportDocPhotoGuidance/>
            </div>

            <!-- Page: ID upload -->
            <div v-if="form.state.activePage == 'supportDocUpload'">
                <h2 class="pageHeading">Please upload photos / scans of any existing ID documents, or evidence of income and expenses, which you have available</h2>

                <ListEditor class="mt-3" headerLabel="Document Uploads"
                    :items="moduleState.supportDocuments"
                    itemIcon="fa-paperclip" :itemLabel="(item)=>`${item.type} ${item.back ? '(front and back)' : '(front only)'}`"
                    addLabel="Add Document" :onAdd="()=>supportDocuments.add()"
                    :onEdit="(idx)=>supportDocuments.edit(idx)" :onRemove="(idx)=>supportDocuments.remove(idx)" />
                
                <ModalDialog
                  v-if="supportDocuments.activeItem" active
                  title="Document Upload" severity="link"
                  firstButtonText="Cancel" firstButtonSeverity="danger" @firstbuttonselected="supportDocuments.activeItem = null"
                  secondButtonText="Save"  secondButtonSeverity="link"  @secondbuttonselected="supportDocuments.onEditComplete()">

                    <div v-for="(item, idx) in [supportDocuments.activeItem]" :key="idx /* Defining 'item' alias */" class="modalScrollContainer">

                        <Columns>
                            <FormTextField label="What type of document is this? *" placeholder="e.g. Birth Record, State ID, Veteran's ID, Driver's License..." v-model="item.type" trimmed autoFocus/>
                        </Columns>
                            <div>
                                <label class="label">Front Image *</label>
                                <DocumentUploadWidget :singleDocument="item.front" @upload="uploadSupportDocument(item as SupportDocument, 'front', $event)" @delete="item.front = undefined"/>
                            </div>

                            <div class="mt-5">
                                <label class="label">Back Image (optional)</label>
                                <DocumentUploadWidget :singleDocument="item.back" @upload="uploadSupportDocument(item as SupportDocument, 'back', $event)" @delete="item.back = undefined"/>
                            </div>


                        <div v-if="supportDocuments.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                            <p v-for="error in supportDocuments.errors" :key="error">{{ error }}</p>
                        </div>
                        <div v-else class="mt-6"></div>
                    </div>
                </ModalDialog>
            </div>

            <!-- Page: Equifax consent -->
            <div v-if="form.state.activePage == 'equifaxConsent'">
                <h2 class="pageHeading">Please let us know if you agree to us verifying your identity</h2>

                <p>Do we have your permission to verify your identity with Equifax?</p>
                <p>This will require us to share some of your information with them.</p>
                <br/>
                <p><strong>Verifying your identity may allow us to speed up your application, and improve the chance of a successful result.</strong></p>
                <br/>
                <p>However, you may opt-out if you'd prefer.</p>
                
                <FormRadioGroupField class="mt-3" label="Do you agree to us verifying your identity? *" :options="EquifaxConsentOptions" v-model="moduleState.application.equifaxConsent"/>
            </div>

            <!-- Page: Notification preferences -->
            <div v-if="form.state.activePage == 'notificationPreferences'">
                <h2 class="pageHeading">Please let us know how we should keep you up-to-date about your application</h2>

                <FormCheckboxField boxLabel="By Email" v-model="moduleState.miscState.notificationPreferences.emailEnabled" :isDisabled="moduleState.miscState.notificationPreferences.noneEnabled"/>
                <HalfColumn v-if="moduleState.miscState.notificationPreferences.emailEnabled && !moduleState.miscState.notificationPreferences.noneEnabled">
                    <FormTextField label="Email Address" v-model="moduleState.notificationPreferences.email" trimmed autoFocus/>
                </HalfColumn>

                <FormCheckboxField boxLabel="By SMS" v-model="moduleState.miscState.notificationPreferences.smsEnabled" :isDisabled="moduleState.miscState.notificationPreferences.noneEnabled"/>
                <HalfColumn v-if="moduleState.miscState.notificationPreferences.smsEnabled && !moduleState.miscState.notificationPreferences.noneEnabled">
                    <FormTextField label="Phone Number" v-model="moduleState.notificationPreferences.sms" trimmed autoFocus/>
                </HalfColumn>

                <FormCheckboxField boxLabel="Do not contact me" v-model="moduleState.miscState.notificationPreferences.noneEnabled"/>

                <div v-if="moduleState.miscState.notificationPreferences.noneEnabled" class="notification bulmaNotification is-warning mt-4">
                    <p>If you don't provide any contact information, you will need to speak to your caseworker for updates on your application</p>
                </div>
            </div>

            <!-- Page: Signature -->
            <div v-else-if="form.state.activePage == 'signature'">
                <h2 class="pageHeading">We're almost done!</h2>
                <HalfColumn>
                    <SignaturePadField style="flex-grow: 1.8" ref="applicantSignaturePad" label="Please sign here to complete your application"/>
                </HalfColumn>

                <FormCheckboxField boxLabel="I have difficulty signing, and require assistance" v-model="moduleState.miscState.signature.witnessRequired"/>

                <div v-if="moduleState.miscState.signature.witnessRequired">
                    <p class="mt-2">Please ask for somebody to assist you and act as a witness.</p>
                    <p class="mt-2">They can help you to sign above with an 'X'.</p>

                    <HalfColumn class="mt-3">
                        <SignaturePadField style="flex-grow: 1.8" ref="witnessSignaturePad" label="Witness - please sign your own name below"/>
                    </HalfColumn>
                </div>
            </div>

            <!-- Page: Pre-submit -->
            <div v-else-if="form.state.activePage == 'preSubmit'">
                <h2 class="pageHeading" style="text-align: center">Ok! We're ready to submit your application</h2>

                <div class="is-flex is-flex-direction-column is-align-items-center">
                    <LoadingSpinner v-if="submissionInProgress" class="mt-4"/>
                    <SaveButton v-else large label="Submit!" icon="fa-paper-plane" @click="submitApplication()"/>
                </div>
            </div>

            <!-- Page: Completed confirmation -->
            <div v-else-if="form.state.activePage == 'completed'">
                <h1>Congratulations! Your application has been submitted</h1>
                <h2 class="pageHeading">You can return to the home page to view your applications.</h2>

                <div class="is-flex is-flex-direction-column is-align-items-center">
                    <SaveButton large label="Return To Home Page" @click="$router.push('/')"/>
                </div>
            </div>

            <!-- Footer / Navigation section -->
            <div v-if="form!.state.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                <p v-for="error in form.state.errors" :key="error">{{ error }}</p>
            </div>

            <div v-if="!submissionInProgress" class="is-flex is-flex-direction-row mt-6">
                <SaveButton v-if="form.canNavigateBack()" large label="Back" icon="fa-chevron-left" @click="form.navigateBack()"/>
                <div class="is-flex-grow-1"></div>
                <SaveButton v-if="form.canNavigateForward()" large label="Next" icon="fa-chevron-right" iconRight @click="form.navigateForward()"/>
            </div>    

        </div>
    </AppPageFrame>
</template>
  
<script lang="ts" setup>
import { 
    moduleState, initNewApplication, submitModule,
    sections, type Page, type SupportDocument,
    EquifaxConsentOptions
} from './module';

import { Core, Feature, GeorgiaDfcsBenefits, ProfileTypes } from '@minicity/core';
import { isModuleEnabled, sessionState } from '@/core/session';
import { uploadFile } from '@/core/api';

import LoadingSpinner from '@/components/LoadingSpinner.vue';
import LoadingPage from '@/components/LoadingPage.vue';
import RequiredAssistanceEditor from './components/RequiredAssistanceEditor.vue';
import ShelterOrUtilityExpenseEditor from './components/ShelterOrUtilityExpenseEditor.vue';
import AuthorizedRepresentativeEditor from './components/AuthorizedRepresentativeEditor.vue';

import { useForm, Form, PageSpecs } from '@/composables/useForm';
import { Validations } from '@/composables/useValidation';

import ListEditor from '@/components/ListEditor.vue';
import { useEditableList } from '@/composables/useEditableList';

import { computed, onMounted, onUnmounted, ref } from 'vue';
import { createDraftApplication, getDraftApplication, updateDraftApplicationContent } from '../api';
import { DeepPartial, formatAge, formatName, mapHttpError, notifyError } from '@/core/utils';
import { notifyNetworkError } from '@/core/utils';
import { getApplicationStatus } from '@/features/applicationStatus/api';

import { InteractionType, logFeatureInteraction } from '@/features/analytics/api';
import FormSelectField from '@/components/FormSelectField.vue';
import SignaturePadField from "@/components/SignaturePadField.vue";
import SupportDocPhotoGuidance from "../components/SupportDocPhotoGuidance.vue";


const draftId = ref<string|null>(null);

/*
 * Define any special logic for each page
 */
 type QuestionSpec<Answers> = {name: string, trigger: keyof Answers, followUps: (keyof Answers)[]};
function validateAnswers<Answers>(answers: Answers, questionSpecs: QuestionSpec<Answers>[]) {
    for (const spec of questionSpecs) {
        const triggerAnswer = answers[spec.trigger];
        if (triggerAnswer === false || triggerAnswer === 0 || triggerAnswer === "0") {
            for (const followUpKey of spec.followUps) {
                (answers as any)[followUpKey] = undefined;
            }
        } else if (triggerAnswer) {
            for (const followUpKey of spec.followUps) {
                const answer = answers[followUpKey];
                if (answer === undefined || answer === null || answer === "") return [`Please answer all parts for question '${spec.name}'`];
            }
        } else return [`Please answer question '${spec.name}'`];
    }
}

const pages: PageSpecs<Page> = {
    "introVideo": {
        nextPage: "selectBenefitTypes"
    },
    "selectBenefitTypes": {
        validate: ()=>{
            const selectedBenefits = moduleState.value!.application!.selectedBenefits!;

            if (!selectedBenefits.foodStamps && !selectedBenefits.TANF && !selectedBenefits.refugeeCashAssistance && !selectedBenefits.medicaid) {
                return ["Please select at least one benefit"];
            }

            if (selectedBenefits.foodStamps) {
                moduleState.value!.application!.expeditedFoodStampQualification ||= {};
            }
        },
        nextPage: "tanfWaiver",
    },
    "tanfWaiver": {
        enabled: ()=>moduleState.value!.application!.selectedBenefits!.TANF!,
        validate: ()=>{
            if (!moduleState.value!.miscState.tanfWaiver.confirmed) return ["You cannot apply for benefits unless you read and agree to these terms"];
        },
        nextPage: "generalWaiver"
    },
    "generalWaiver": {
        validate: ()=>{
            if (!moduleState.value!.miscState.generalWaiver.confirmed) return ["You cannot apply for benefits unless you read and agree to these terms"];
        },
        nextPage: "authorizedRepresentative"
    },
    "authorizedRepresentative": {
        validate: ()=>{
            if (moduleState.value!.application!.authorizedRepresentatives!.first === null) {
                delete moduleState.value!.signatures[GeorgiaDfcsBenefits.SignatureType.FIRST_REPRESENTATIVE];
                delete moduleState.value!.signatures[GeorgiaDfcsBenefits.SignatureType.SECOND_REPRESENTATIVE];
                moduleState.value!.application!.authorizedRepresentatives = {};
            } else if (moduleState.value!.application!.authorizedRepresentatives!.first === undefined) {
                return (authorizedRep1Field.value as any).getValidationErrors();
            } else {
                const sigData = (authorizedRep1Field.value as any).getSignatureData();
                if (!sigData) return ["Your representative must sign their name"];
                moduleState.value!.signatures[GeorgiaDfcsBenefits.SignatureType.FIRST_REPRESENTATIVE] = sigData;
            }
        },
        nextPage: "authorizedRepresentative2"
    },
    "authorizedRepresentative2": {
        validate: ()=>{
            if (moduleState.value!.application!.authorizedRepresentatives!.second === null) {
                delete moduleState.value!.signatures[GeorgiaDfcsBenefits.SignatureType.SECOND_REPRESENTATIVE];
            } else if (moduleState.value!.application!.authorizedRepresentatives!.second === undefined) {
                return (authorizedRep2Field.value as any).getValidationErrors();
            } else {
                const sigData = (authorizedRep2Field.value as any).getSignatureData();
                if (!sigData) return ["Your representative must sign their name"];
                moduleState.value!.signatures[GeorgiaDfcsBenefits.SignatureType.SECOND_REPRESENTATIVE] = sigData;
            }
        },
        enabled: ()=>Boolean(moduleState.value!.application!.authorizedRepresentatives!.first),
        nextPage: "authorizedRepresentativeSettings"
    },
    "authorizedRepresentativeSettings": {
        enabled: ()=>Boolean(moduleState.value!.application!.authorizedRepresentatives!.first),
        validate: ()=>{
            if (moduleState.value!.application!.authorizedRepresentatives!.toReceiveMedicaidCard == undefined) {
                return ["Please indicate whether your representative(s) require Medicaid cards"];
            }
        },
        nextPage: "basicCitizenInfo"
    },
    "basicCitizenInfo": {
        validate: ()=>{
            const applicant = moduleState.value!.application!.applicant!;
            if (applicant.firstName!.length == 0) return ["Please enter your first name"];
            if (applicant.lastName!.length == 0) return ["Please enter your last name"];
            if (applicant.socialSecurityNumber!.length != 9) return ["Social Security Number should be in the form AAABBCCCC"];
            if (applicant.isUSCitizenOrQualifiedImmigrant == undefined) return ["Please indicate your citizenship / immigration status"];
            if (!applicant.sex) return ["Please select your sex / gender"];
            if (!applicant.dateOfBirth) return ["Please enter your date of birth"];
        },
        nextPage: "address"
    },
    "address": {
        enabled: () => false, // disabled - always using PO box
        onLoad: () => {
            const applicant = moduleState.value!.application!.applicant!;
            if (!applicant.mailingAddress) {
                applicant.mailingAddress = {
                    number: "",
                    street: "",
                    city: "",
                    state: undefined,
                    postalCode: ""
                };
            }
        },
        validate: ()=>{
            const applicant = moduleState.value!.application!.applicant!;
            
            if (applicant.useCareProviderAddressForMailing) {
                applicant.mailingAddress = undefined;
                applicant.streetAddress = undefined;
            } else {
                if (applicant.mailingAddress!.number!.length == 0) return ["Please enter your house / building number"];
                if (applicant.mailingAddress!.street!.length == 0) return ["Please enter your street name"];
                if (applicant.mailingAddress!.city!.length == 0) return ["Please enter your city"];
                if (applicant.mailingAddress!.postalCode!.length == 0) return ["Please enter your zip code"];
                if (!applicant.mailingAddress!.state) return ["Please select your state"];
            }

            // Copy these across for street address
            applicant.useCareProviderAddressForStreet = applicant.useCareProviderAddressForMailing;
            applicant.streetAddress = applicant.mailingAddress;
        },
        nextPage: "contactInfo"
    },
    "contactInfo": {
        validate: ()=>{
            const applicant = moduleState.value!.application!.applicant!;
            if (applicant.smsCommunicationPreferred) {
                if (applicant.mainPhone!.length == 0) return ["Please enter your phone number"];
            } else {
                applicant.mainPhone = undefined;
            }

            if (applicant.emailCommunicationPreferred) {
                if (!Validations.email(applicant.email)) return ["Please enter a valid email address"];
            } else {
                applicant.email = undefined;
            }
        },
        nextPage: "communicationPreferences"
    },
    "communicationPreferences": {
        validate: ()=>{
            const applicant = moduleState.value!.application!.applicant!;

            if (applicant.preferredLanguage?.length == 0) return ["Please indicate your preferred language"];
        },
        nextPage: "householdMembers"
    },
    "householdMembers": {
        nextPage: "foodStampsQualificationQuestions"
    },
    "foodStampsQualificationQuestions": {
        enabled: ()=>moduleState.value!.application!.selectedBenefits!.foodStamps!,
        validate: ()=>{
            const qs = moduleState.value!.application!.expeditedFoodStampQualification!;

            const questionSpecs:QuestionSpec<typeof qs>[] = [
                {name: "Are you, or any household members, migrant or seasonal farm workers?", trigger: "isMigrantOrSeasonalFarmWorker", followUps: ["whoMigrantOrSeasonalFarmWorker"]},
                {name: "Have you received energy assistance in the last 12 months?", trigger: "hasReceivedEnergyAssistance", followUps: ["energyAssistanceAmount"]},
                {name: "What is your total earned income expected for this month?", trigger: "totalEarnedIncomeThisMonth", followUps: ["employerName", "employmentHoursPerWeek", "employmentBeginDate", "employmentEndDate", "employmentPayRate", "earnedIncomeFrequency"]},
                {name: "What is your total unearned income expected for this month", trigger: "totalUnearnedIncomeThisMonth", followUps: ["firstUnearnedIncomeType", "firstUnearnedIncomeAmount", "firstUnearnedIncomeFrequency"]},
                {name: "What is your total earned and unearned income expected for this month?", trigger: "totalIncomeThisMonth", followUps: []},
                {name: "How much money do you and all household members have in cash or in the bank?", trigger: "totalMoneyInCashAndBank", followUps: []},
                {name: "What is the monthly total you pay for rent, mortgage, property taxes, and/or homeowners insurance?", trigger: "totalRentOrMortgagePayment", followUps: []},
                {name: "What is the monthly total you pay for electric, water, gas, and/or other utilities?", trigger: "totalUtilitiesPayment", followUps: []},
                {name: "Have you received energy assistance in the last 12 months?", trigger: "hasReceivedEnergyAssistance", followUps: ["energyAssistanceAmount"]},
            ];

            if (qs.totalUnearnedIncomeThisMonth != 0 && moduleState.value!.miscState.foodStampsQualificationQuestions.hasSecondUnearnedIncome) {
                questionSpecs.push(
                    {name: "I have another source of unearned income", trigger: "totalUnearnedIncomeThisMonth", followUps: ["secondUnearnedIncomeType", "secondUnearnedIncomeAmount", "secondUnearnedIncomeFrequency"]}
                );
            } else {
                for (const q of ["secondUnearnedIncomeType", "secondUnearnedIncomeAmount", "secondUnearnedIncomeFrequency"]) {
                    (qs as any)[q] = undefined;
                }
            }

            return validateAnswers(qs, questionSpecs);
        },
        nextPage: "additionalQualificationQuestions"
    },
    "additionalQualificationQuestions": {
        validate: ()=>{
            const qs = moduleState.value!.application!.additionalQualifications!;
            const selectedBenefits = moduleState.value!.application!.selectedBenefits!;

            const questionSpecs:QuestionSpec<typeof qs>[] = [
                {name: "Has anyone received any benefits in another county or state?", trigger: "hasBenefitsFromOtherCountyOrState", followUps: ["whoBenefitsFromOtherCountyOrState", "whereBenefitsFromOtherCountyOrState", "whenBenefitsFromOtherCountyOrState"]},
            ];

            if (selectedBenefits.foodStamps) {
                questionSpecs.push(
                    {name: "Has anyone been convicted of giving false information about where they live or who they are to get multiple Food Stamps benefits in more than one area after 8/22/96?", trigger: "hasFalseInformationBenefitsConvicion", followUps: ["whoFalseInformationBenefitsConvicion", "whereFalseInformationBenefitsConvicion", "whenFalseInformationBenefitsConvicion"]},
                    {name: "Has anyone been convicted of trading Food Stamp benefits for drugs after 8/22/96?", trigger: "hasFoodStampDrugTradingConviction", followUps: ["whoFoodStampDrugTradingConviction", "whenFoodStampDrugTradingConviction"]},
                    {name: "Has anyone been convicted of buying or selling Food Stamp benefits over $500 after 8/22/96?", trigger: "hasFoodStampSellingConviction", followUps: ["whoFoodStampSellingConviction", "whenFoodStampSellingConviction"]},
                    {name: "Have you or any household member been convicted of trading Food Stamp benefits for guns, ammunition or explosives after 8/22/96?", trigger: "hasFoodStampWeaponsTradingConviction", followUps: ["whoFoodStampWeaponsTradingConviction", "whenFoodStampWeaponsTradingConviction"]},
                    {name: "Has anyone received lottery or gambling winnings?", trigger: "hasGamblingWinnings", followUps: ["whoGamblingWinnings", "whenGamblingWinnings", "gamblingWinningsAmount"]},
                );
            }

            if (selectedBenefits.foodStamps || selectedBenefits.TANF) {
                questionSpecs.push(
                    {name: "Did anyone in your household voluntarily quit a job, or voluntarily reduce their work hours below 30 hours per week, within the last 30 days?", trigger: "hasVoluntarilyQuitOrReducedHours", followUps: ["whoVoluntarilyQuitOrReducedHours", "whyVoluntarilyQuitOrReducedHours"]},
                    {name: "Is anyone disqualified from the Food Stamp or TANF Program?", trigger: "hasFoodStampOrTANFDisqualification", followUps: ["whoFoodStampOrTANFDisqualification", "whereFoodStampOrTANFDisqualification"]},
                    {name: "Is anyone trying to avoid prosecution or jail for a felony?", trigger: "isAvoidingProsecutionOrJail", followUps: ["whoAvoidingProsecutionOrJail"]},
                    {name: "Is anyone violating conditions of probation or parole?", trigger: "hasParoleViolation", followUps: ["whoParoleVoliation"]},
                    {name: "Does anyone have a violent or drug-related felony conviction after 8/22/96?", trigger: "hasFelonyDrugOrViolenceConviction", followUps: ["whoFelonyDrugOrViolenceConviction", "whenFelonyDrugOrViolenceConviction", "isComplyingWithFelonyDrugOrViolenceConvictionProbationTerms", "isComplyingWithFelonyDrugOrViolenceConvictionParoleTerms", "hasCompletedAllFelonyDrugOrViolenceConvictionTerms"]},
                );
            }

            if (selectedBenefits.medicaid) {
                questionSpecs.push(
                    {name: "Does anyone have any unpaid medical bills for the last 3 months?", trigger: "hasUnpaidMedicalBills", followUps: []},
                );
            }

            if (selectedBenefits.medicaid || selectedBenefits.TANF) {
                questionSpecs.push(
                    {name: "Is anyone pregnant?", trigger: "isPregnant", followUps: ["whoPregnant", "pregnancyDueDate"]},
                );
            }

            if (selectedBenefits.TANF) {
                questionSpecs.push(
                    {name: "Has anyone used TANF funds or the EPPIC Card at liquor stores, casinos, poker rooms, adult entertainment business, bail bonds, night clubs, salons/taverns, bingo halls, race tracks, gun/ammunition stores, cruise ships, psychic readers, smoking shops, tattoo/piercing shops or spa/massage salons?", trigger: "hasMisusedTANF", followUps: ["whoMisusedTANF", "whenMisusedTANF"]},
                );
            }

            return validateAnswers(qs, questionSpecs);
        },
        nextPage: "income"
    },
    "income": {
        nextPage: "careExpenses"
    },
    "careExpenses": {
        validate: () => {
            if (moduleState.value!.miscState.expenses.hasCareExpenses == undefined) return ["Please indicate whether there are care expenses"];
            else if (!moduleState.value!.miscState.expenses.hasCareExpenses) {
                moduleState.value!.application.expenses!.careExpenses = [];
            }
        },
        nextPage: "dependentTransportationExpenses"
    },
    "dependentTransportationExpenses": {
        validate: () => {
            const expenses = moduleState.value!.application!.expenses!;
            if (expenses.hasDependentTransportationExpenses == undefined) return ["Please indicate whether you pay transport expenses for a dependent"];
            
            if (expenses.hasDependentTransportationExpenses) {
                if (moduleState.value!.miscState.expenses.hasCareExpenses) {
                    if (expenses.areDependentTransportationExpensesIncluded == undefined) return ["Please indicate whether you have included these expenses on the previous screen"];

                    if (expenses.areDependentTransportationExpensesIncluded) {
                        expenses.totalMilesDrivenWeekly = undefined;
                    } else {
                        if (!expenses.totalMilesDrivenWeekly) return ["Please indicate the total number of miles driven weekly"];
                    }
                } else {
                    expenses.areDependentTransportationExpensesIncluded = false;
                    if (!expenses.totalMilesDrivenWeekly) return ["Please indicate the total number of miles driven weekly"];
                }
            } else {
                expenses.areDependentTransportationExpensesIncluded = undefined;
                expenses.totalMilesDrivenWeekly = undefined
            }
        },
        nextPage: "medicalExpenses"
    },
    "medicalExpenses": {
        validate: () => {
            if (moduleState.value!.miscState.expenses.hasMedicalExpenses == undefined) return ["Please indicate whether there are medical expenses"];
            else if (!moduleState.value!.miscState.expenses.hasMedicalExpenses) {
                moduleState.value!.application.expenses!.medicalExpenses = [];
            }
        },
        nextPage: "medicalTransportationExpenses"
    },
    "medicalTransportationExpenses": {
        validate: () => {
            if (moduleState.value!.miscState.expenses.hasMedicalTransportationExpenses == undefined) return ["Please indicate whether there are medical transportation expenses"];
            else if (!moduleState.value!.miscState.expenses.hasMedicalTransportationExpenses) {
                moduleState.value!.application.expenses!.medicalTransportationExpenses = [];                
            }
        },
        nextPage: "childSupportExpenses"
    },
    "childSupportExpenses": {
        validate: () => {
            if (moduleState.value!.miscState.expenses.hasChildSupportExpenses == undefined) return ["Please indicate whether there are child support expenses"];
            else if (!moduleState.value!.miscState.expenses.hasChildSupportExpenses) {
                moduleState.value!.application.expenses!.childSupportExpenses = [];                
            }
        },
        nextPage: "shelterOrUtilityExpenses"
    },
    "shelterOrUtilityExpenses": {
        onLoad() {
            if (!moduleState.value!.application!.expenses!.shelterOrUtilityExpenses) {
                moduleState.value!.application!.expenses!.shelterOrUtilityExpenses = {};
            }
        },
        validate() {
            if (moduleState.value!.miscState.expenses.hasShelterOrUtilityExpenses == undefined) return ["Please indicate whether there are shelter or utility expenses"];
            else if (!moduleState.value!.miscState.expenses.hasShelterOrUtilityExpenses) {
                moduleState.value!.application!.expenses!.shelterOrUtilityExpenses = undefined;
                moduleState.value!.application!.expenses!.sharedExpenses = undefined;
                moduleState.value!.application!.expenses!.otherBillPayer = undefined;
            } else {
                const expenses = moduleState.value!.application!.expenses!.shelterOrUtilityExpenses!;
                if (expenses.rentOrMortgage === undefined) return ["Please provide information about your rent / mortgage payments"];
                if (expenses.propertyTax === undefined) return ["Please provide information about your property tax payments"];
                if (expenses.propertyInsurance === undefined) return ["Please provide information about your property insurance payments"];
                if (expenses.electricity === undefined) return ["Please provide information about your electricity payments"];
                if (expenses.gas === undefined) return ["Please provide information about your gas payments"];
                if (expenses.garbage === undefined) return ["Please provide information about your garbage disposal payments"];
                if (expenses.telephone === undefined) return ["Please provide information about your telephone payments"];
                if (expenses.other === undefined) return ["Please provide information about any other shelter or utility payments"];
            }
        },
        nextPage: "sharedExpenses"
    },
    "sharedExpenses": {
        enabled: ()=>moduleState.value!.miscState.expenses.hasShelterOrUtilityExpenses,
        onLoad() {
            if (!moduleState.value!.application!.expenses!.sharedExpenses) {
                moduleState.value!.application!.expenses!.sharedExpenses = {
                    landlordAddress: {number: "", street: "", city: "", state: undefined, postalCode: ""}
                };
            }
        },
        validate()
         {
            if (moduleState.value!.miscState!.expenses!.hasSharedExpenses == undefined) return ["Please indicate whether there are shared expenses"];
            else if (!moduleState.value!.miscState.expenses.hasSharedExpenses) {
                moduleState.value!.application!.expenses!.sharedExpenses = undefined;
            } else {
                const sharedExpenses = moduleState.value!.application!.expenses!.sharedExpenses!;
                if (sharedExpenses.who?.length == 0) return ["Please indicate who you share expenses with"];
                if (sharedExpenses.comments?.length == 0) return ["Please describe the arrangement"];
                if (sharedExpenses.recipientName?.length == 0) return ["Please indicate who you pay to"];
                if (!sharedExpenses.paymentAmount) return ["Please indicate the amount paid"];
                if (!sharedExpenses.paymentFrequency) return ["Please indicate the payment frequency"];
                if (sharedExpenses.landlordName?.length == 0) return ["Please indicate the landlord's name"];
            }
        },
        nextPage: "otherBillPayer"
    },
    "otherBillPayer": {
        enabled: ()=>moduleState.value!.miscState.expenses.hasShelterOrUtilityExpenses,
        onLoad() {
            if (!moduleState.value!.application!.expenses!.otherBillPayer) {
                moduleState.value!.application!.expenses!.otherBillPayer = {};
            }
        },
        validate() {
            if (moduleState.value!.miscState!.expenses!.hasOtherBillPayer == undefined) return ["Please indicate whether there is another bill payer"];
            else if (!moduleState.value!.miscState.expenses.hasOtherBillPayer) {
                moduleState.value!.application!.expenses!.otherBillPayer = undefined;
            } else {
                const otherBillPayer = moduleState.value!.application!.expenses!.otherBillPayer!;
                if (otherBillPayer.payerName?.length == 0) return ["Please indicate the bill payer's name"];
                if (otherBillPayer.whichBills?.length == 0) return ["Please indicate which bills are paid"];
                if (otherBillPayer.amount?.length == 0) return ["Please indicate the amount paid"];
                if (otherBillPayer.recipientName?.length == 0) return ["Please indicate the person to whom the bills are paid"];
            }
        },
        nextPage: "supportDocGuidance"
    },
    "supportDocGuidance": {
        nextPage: "supportDocUpload"
    },
    "supportDocUpload": {
        nextPage: "equifaxConsent"
    },
    "equifaxConsent": {
        enabled: ()=>isModuleEnabled(Feature.Type.EQUIFAX_CHECKS),
        validate: ()=>{
            const appl = moduleState.value!.application!;
            if (appl.equifaxConsent === undefined) return ["Please indicate whether you agree to us verifying your identity"];
        },
        nextPage: "notificationPreferences"
    },
    "notificationPreferences": {
        validate: ()=>{
            const prefs = moduleState.value!.notificationPreferences!;
            const pageState = moduleState.value!.miscState.notificationPreferences;

            if (pageState.noneEnabled) {
                prefs.email = undefined;
                prefs.sms = undefined;
            } else {
                if (!pageState.emailEnabled && !pageState.smsEnabled) return ["Please indicate how you would like to receive updates about your application"];

                if (pageState.emailEnabled) {
                    if (!Validations.email(prefs.email)) return ["Please enter a valid email address"];
                } else {
                    prefs.email = undefined;
                }

                if (pageState.smsEnabled) {
                    if (!Validations.phone_US(prefs.sms)) return ["Please enter a valid US phone number"];
                } else {
                    prefs.sms = undefined;
                }
            }
        },
        nextPage: "signature"
    },
    "signature": {
        validate: ()=>{
            const sigData = (applicantSignaturePad.value as any).getSignatureData();
            if (!sigData) return ["You must sign your name"];
            moduleState.value!.signatures[GeorgiaDfcsBenefits.SignatureType.CITIZEN] = sigData;

            if (moduleState.value!.miscState.signature.witnessRequired) {
                const sigData = (witnessSignaturePad.value as any).getSignatureData();
                if (!sigData) return ["Your witness must sign their name"];
                moduleState.value!.signatures[GeorgiaDfcsBenefits.SignatureType.WITNESS] = sigData;
            } else {
                delete moduleState.value!.signatures[GeorgiaDfcsBenefits.SignatureType.WITNESS];
            }
        },
        nextPage: "preSubmit"
    },
    "preSubmit": {},
    "completed": {disallowBack: true},
};


/*
 * Initialize
 */
let availableApplicationTypes: string[] = [];

 onMounted(() => {
    if (!sessionState.session) return;
    
    getApplicationStatus(sessionState.session.user.id)
    .then(applStatus => {
        // Determine which application types are available
        // The module must be enabled for the citizen's care provider, and they must not already have an application in-progress
        if (isModuleEnabled('BIRTH_RECORDS') && !applStatus.applications.find(appl => appl.applicationType == 'birthRecords')) {
            availableApplicationTypes.push('birthRecords');
        }

        if (isModuleEnabled('SOCIAL_SECURITY') && !applStatus.applications.find(appl => appl.applicationType == 'socialSecurity')) {
            availableApplicationTypes.push('socialSecurity');
        }
        
        // Load or start a new application
        getDraftApplication(sessionState.session!.user.id, "georgiaDfcsBenefits")
        .then(draft => {
            console.log(`Resumed draft application ${draft.id}`, draft);

            moduleState.value = JSON.parse(draft.content);
            draftId.value = draft.id;
            initFormNavigation();
        })
        .catch(err => {
            console.error("Failed to resume application", err);
            startNewApplication();
        });
    });
});

const startNewApplication = () => {
    console.log("Starting new application");

    moduleState.value = initNewApplication({
        // Initial misc state
        notificationPreferences: {
            emailEnabled: sessionState.session!.user.preferences.notifications.email != undefined,
            smsEnabled: sessionState.session!.user.preferences.notifications.sms != undefined,
            noneEnabled: false,
        },
        foodStampsQualificationQuestions: {
            hasSecondUnearnedIncome: false
        },
        tanfWaiver: {confirmed: false},
        generalWaiver: {confirmed: false},
        expenses: {},
        signature: {witnessRequired: false},
    });

    createDraftApplication(sessionState.session!.user.id, "georgiaDfcsBenefits", moduleState.value)
    .then(createdDraftId => {
        draftId.value = createdDraftId;
        initFormNavigation();
    })
    .catch(err => notifyError(err))
}

let formUpdateTimer: NodeJS.Timeout|null = null; 

const initFormNavigation = () => {
    form.value = useForm(pages, sections, moduleState.value!.formNavState, onFormChanged);

    // Set a timer to regularly push updated form state to server (also keeps session alive)
    const updateTimer = () => {
        console.log("Update timer triggered");
        onFormChanged();
        formUpdateTimer = setTimeout(updateTimer, 30000);
    };
    formUpdateTimer = setTimeout(updateTimer, 30000);

    // Generate analytics event
    logFeatureInteraction("applications/georgiaDfcsBenefits", "GEORGIA_DFCS_BENEFITS", InteractionType.CLICK, JSON.stringify({'draftId': draftId.value, 'page': form.value!.state.activePage, 'errors': form.value!.state.errors}));
}

onUnmounted(() => {
    if (formUpdateTimer) {
        console.log("Diabled update timer", formUpdateTimer);
        clearTimeout(formUpdateTimer);
    }
});

/*
 * Form state
 */
const form = ref<Form<Page>|null>(null);

const currentSection = computed(() => {
    if (!moduleState.value) return null;
    return form.value!.determineCurrentSection();
});

// Element / component references
const authorizedRep1Field = ref(null);
const authorizedRep2Field = ref(null);
const applicantSignaturePad = ref(null);
const witnessSignaturePad = ref(null);

/*
 * Draft saving / form submission
 */
let lastPushedModuleStateJson = "";
const onFormChanged = () => {
    const moduleStateJson = JSON.stringify(moduleState.value);
    if (moduleStateJson == lastPushedModuleStateJson) return; // No change - no need to update

    console.log("Pushing updated form state to server", moduleState.value);

    updateDraftApplicationContent(sessionState.session!.user.id, draftId.value!, moduleState.value)
    .then(() => lastPushedModuleStateJson = moduleStateJson);

    // Generate analytics event
    logFeatureInteraction("applications/georgiaDfcsBenefits", "GEORGIA_DFCS_BENEFITS", InteractionType.PROGRESS, JSON.stringify({'draftId': draftId.value, 'page': form.value!.state.activePage, 'errors': form.value!.state.errors}));
};

const submissionInProgress = ref<boolean>(false);

const submitApplication = async () => {
    form.value!.state.errors = [];
    console.log("Submitting", moduleState.value!.application);

    submissionInProgress.value = true;
    try {
        await submitModule(draftId.value!);
        form.value!.displayPage("completed");

        // Generate analytics event
        logFeatureInteraction("applications/georgiaDfcsBenefits/submit", "GEORGIA_DFCS_BENEFITS", InteractionType.COMPLETION, JSON.stringify({'draftId': draftId.value, 'page': form.value!.state.activePage}));
    } catch (ex) {
        console.log(ex);
        form.value!.state.errors = [`Failed to submit - ${mapHttpError(ex)}`];

        // Generate analytics event
        logFeatureInteraction("applications/georgiaDfcsBenefits/submit", "GEORGIA_DFCS_BENEFITS", InteractionType.ERROR, JSON.stringify({'draftId': draftId.value, 'errors': form.value!.state.errors}));
    } finally {
        submissionInProgress.value = false;
    }
};

/*
 * List editors
 */
 const householdMembers = ref(useEditableList<GeorgiaDfcsBenefits.HouseholdMember>(
    () => moduleState.value!.application.householdMembers! as DeepPartial<GeorgiaDfcsBenefits.HouseholdMember>[], // Items
    () => { return {} }, // Create new item

    // Validate
    (item: DeepPartial<GeorgiaDfcsBenefits.HouseholdMember>) => {
        if (item.firstName!.length == 0) return ["Please enter first name"];
        if (item.lastName!.length == 0) return ["Please enter last name"];
        if (!item.dateOfBirth) return ["Please enter date of birth"];
        if (!item.relationship) return ["Please select this person's relationship to you"];
        if (!item.sex) return ["Please select sex / gender"];
        if (!item.race) return ["Please select race / ethnicity"];

        if (item.isApplyingForBenefits == undefined) return ["Please indicate whether this person is applying for benefits"];

        if (item.isApplyingForBenefits) {
            if (item.socialSecurityNumber!.length != 9) return ["Social Security Number should be in the form AAABBCCCC"];
            if (item.isUSCitizenOrQualifiedImmigrant == undefined) return ["Please indicate citizenship / immigration status"];
        } else {
            item.socialSecurityNumber = undefined;
            item.isUSCitizenOrQualifiedImmigrant = undefined;
        }

        return [];
    },

    // On change
    onFormChanged
));

const incomeSources = ref(useEditableList<GeorgiaDfcsBenefits.Income>(
    () => moduleState.value!.application.income! as DeepPartial<GeorgiaDfcsBenefits.Income>[], // Items
    () => { return {} }, // Create new item

    // Validate
    (item: DeepPartial<GeorgiaDfcsBenefits.Income>) => {
        if (item.name!.length == 0) return ["Please indicate who receives the income"];
        if (item.type!.length == 0) return ["Please enter income type"];
        if (item.source!.length == 0) return ["Please enter employer name / income source"];
        if (!item.monthlyAmount) return ["Please enter monthly amount"];
        if (!item.frequency) return ["Please enter frequency"];
        if (item.datesPaid!.length == 0) return ["Please indicate when the income is paid"];
        return [];
    },

    // On change
    onFormChanged
));

const careExpenses = ref(useEditableList<GeorgiaDfcsBenefits.CareExpense>(
    () => moduleState.value!.application.expenses!.careExpenses! as DeepPartial<GeorgiaDfcsBenefits.CareExpense>[], // Items
    () => { return {} }, // Create new item

    // Validate
    (item: DeepPartial<GeorgiaDfcsBenefits.CareExpense>) => {
        if (item.recipientName!.length == 0) return ["Please indicate who receives the care"];
        if (item.payerName!.length == 0) return ["Please indicate who pays for the care"];
        if (item.reason!.length == 0) return ["Please enter reason / type of care"];
        if (item.provider!.length == 0) return ["Please enter provider information"];
        if (!item.amount) return ["Please enter amount paid"];
        if (!item.frequency) return ["Please enter payment frequency"];
        return [];
    },

    // On change
    onFormChanged
));

const medicalExpenses = ref(useEditableList<GeorgiaDfcsBenefits.MedicalExpense>(
    () => moduleState.value!.application.expenses!.medicalExpenses! as DeepPartial<GeorgiaDfcsBenefits.MedicalExpense>[], // Items
    () => { return {} }, // Create new item

    // Validate
    (item: DeepPartial<GeorgiaDfcsBenefits.MedicalExpense>) => {
        if (item.name!.length == 0) return ["Please indicate who has this expense"];
        if (item.type!.length == 0) return ["Please enter type of expense"];
        if (!item.amount) return ["Please enter amount of expense"];
        if (item.stillOwed === undefined) return ["Please indicate whether this expense is still owed"];
        if (item.willInsurancePay === undefined) return ["Please indicate whether the expense is covered by insurance"];
        return [];
    },

    // On change
    onFormChanged
));

const medicalTransportationExpenses = ref(useEditableList<GeorgiaDfcsBenefits.MedicalTransportationExpense>(
    () => moduleState.value!.application.expenses!.medicalTransportationExpenses! as DeepPartial<GeorgiaDfcsBenefits.MedicalTransportationExpense>[], // Items
    () => { return {} }, // Create new item

    // Validate
    (item: DeepPartial<GeorgiaDfcsBenefits.MedicalTransportationExpense>) => {
        if (item.purpose!.length == 0) return ["Please enter the purpose of the trip"];
        if (!item.milesDriven) return ["Please enter the miles travelled"];
        if (!item.cost) return ["Please enter the cost"];
        return [];
    },

    // On change
    onFormChanged
));

const childSupportExpenses = ref(useEditableList<GeorgiaDfcsBenefits.ChildSupportExpense>(
    () => moduleState.value!.application.expenses!.childSupportExpenses! as DeepPartial<GeorgiaDfcsBenefits.ChildSupportExpense>[], // Items
    () => { return {} }, // Create new item

    // Validate
    (item: DeepPartial<GeorgiaDfcsBenefits.ChildSupportExpense>) => {
        if (item.childName!.length == 0) return ["Please indicate the child's name"];
        if (item.payerName!.length == 0) return ["Please indicate who pays"];
        if (!item.obligatedAmount) return ["Please enter the amount they are obligated to pay"];
        if (!item.actualAmount) return ["Please enter the amount which was actually paid"];
        if (item.recipientName!.length == 0) return ["Please indicate who receives the payment"];
        return [];
    },

    // On change
    onFormChanged
));

const supportDocuments = ref(useEditableList<SupportDocument>(
    () => moduleState.value!.supportDocuments as DeepPartial<SupportDocument>[], // Items
    () => { return {} }, // Create new item

    // Validate
    (item: DeepPartial<SupportDocument>) => {
        if (item.type!.length == 0) return ["Please enter the document type"];
        if (!item.front) return ["Please upload the front image"];
        return [];
    },

    // On change
    onFormChanged
));

const uploadSupportDocument = (sd: SupportDocument, side: "front"|"back", uploadInfo: {file: File, onDone: ()=>any}) => {
    uploadFile(uploadInfo.file, ['IMAGE','PDF'])
    .then(doc => {
        sd[side] = doc;
        supportDocuments.value.errors = [];
    })
    .catch(err => notifyNetworkError("Failed to upload document", err))
    .finally(() => uploadInfo.onDone());
}

</script>
  
<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.formPage {
    max-width: 800px;
    margin: 0px auto;
    padding: 1rem;

    text-align: start;
}

.pageHeading {
    margin-top: 2rem;
    margin-bottom: 2rem;
    opacity: 0.7;
    font-size: 120%;
}

.pageSubHeading {
    margin-top: 2rem;
    margin-bottom: 0.5rem;
    opacity: 0.7;
    font-size: 120%;
}

.infoBox {
    background-color: rgba(0,0,0,0.1);
    padding: 1rem;
    border-radius: 0.5rem;
}

ul {
    list-style: inside;
}

.exampleImageRow {
    text-align: center;
}

.exampleImage {
    margin: 0.75rem;
    width: min(200px, 40%);
}

.contentWrapper {
    max-width: min(100%, 700px);
}

.modalScrollContainer {
    overflow-y: scroll;
    overflow-x: hidden;
    padding-right: 15px;
    max-height: 65vh;
}

.termsBox {
    margin-top: 2rem;
    padding: 1.5rem 2rem;
    border: 2px solid #ddd;
    border-radius: 0.25rem;

    display: flex;
    flex-direction: column;
    align-items: center;

    max-height: 62vh;
}

.termsHeading {
    opacity: 0.7;
    font-size: 120%;
    text-align: center;
}

.termsContent {
    margin-top: 1rem;
    overflow-y: scroll;
    overflow-x: hidden;
    padding-right: 15px;
}

@media screen and (max-width: $tabletWidth) {
    .termsBox {
        padding: 0px;
        border: none;
        max-height: unset;
    }

    .termsContent {
        padding: 0px;
        margin-top: 2rem;
    }
}

</style>

<style>
.modalDialog-content {
  max-height: 90vh !important;
}

.modalDialog-buttonArea {
  flex-direction: row !important;
}

.button:not(:last-child) {
  margin-bottom: 0px !important;
}

.questionDivider {
    margin: 3rem 0;
}
</style>